import { AddCircle, ArrowDropUp } from '@mui/icons-material';
import { Box, Checkbox, CircularProgress, IconButton, Stack, Table, Tooltip, Typography } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { styled } from '@mui/material/styles';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import { QueryKey } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { MarketsGroupedByMarketType, StyleObj } from '../../@types/';
import { Event, Market, Participant, SpecialValue } from '../../@types/api';
import { QUERY_KEYS } from '../../constants';
import { EVENT_TYPE } from '../../constants/enums';
import { useModal } from '../../contexts/ModalContext';
import { getLatestValue } from '../../helpers';
import { useInvalidateQuery } from '../../hooks/useInvalidateQuery';
import useMarketsInfiniteScroll from '../../hooks/useMarketsInfiniteScroll';
import useMutateData from '../../hooks/useMutateData';
import { formatMarketsData } from '../../utils';
import Switch from '../atoms/Switch';
import PublishStatusIcon from '../icons/PublishStatusIcon';
import ManageMarketMenu from '../molecules/ManageMarketMenu';
import OutcomesTable from './OutcomesTable';
import SpecialValueForm from './forms/SpecialValueForm';
import ManualIcon from '../icons/ManualIcon';

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: 2,
  [`&.${tableCellClasses.head}`]: {
    border: '1px solid rgba(0, 83, 55, 0.20)',
    background: theme.palette.background.lightGreen,
    color: theme.palette.background.contrastText,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '120%',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    border: '1px solid',
    borderColor: theme.palette.primary.container,
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
    },
  },
}));

const styles: StyleObj = {
  tableContainer: (theme) => ({
    borderRadius: 0,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  }),
  selectTableCell: {
    width: 48,
  },
  marketNameCellContent: {
    display: 'flex',
    alignItems: 'center',
    pl: 0.75,
    '& h6': {
      pl: 0.25,
    },
  },
  switchWrapper: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  arrowIconWrapper: { cursor: 'pointer', width: '100%', display: 'flex', justifyContent: 'flex-end' },
  outcomeTableCell: {
    p: 0,
  },
  outcomeTableWrapper: (theme) => ({
    width: '95%',
    display: 'flex',
    margin: '0 auto',
    my: 2,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      px: 1,
    },
  }),
  toggleRowCell: {
    cursor: 'pointer',
  },
  mainRowSelected: {
    background: (theme) => theme.palette.selected.yellowLight,
  },
};

const isGroupOfMarkets = (market: Market | MarketsGroupedByMarketType): market is MarketsGroupedByMarketType =>
  'markets' in market;

type RowCollapseMode = {
  id: string;
  isCollapsedMode: boolean;
};

type MarketsTableProps = {
  queryKey: QueryKey;
  event: Event;
  activeTab: string | false;
  search?: string;
};

const MarketsTable = ({ event, queryKey, activeTab, search }: MarketsTableProps) => {
  const [rowsCollapsedMode, setRowsCollapsedMode] = useState<RowCollapseMode[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const { openModal } = useModal();
  const invalidateData = useInvalidateQuery();

  const { createData: createMarketChangesData } = useMutateData('market-changes', [
    QUERY_KEYS.markets,
    {
      eventId: event?.id,
      activeGroup: activeTab,
    },
  ]);

  const { markets, isFetchingNextPage, isLoading, lastElementRef } = useMarketsInfiniteScroll(
    event?.id,
    activeTab,
    search
  );

  useEffect(() => {
    if (markets) {
      setRowsCollapsedMode((prev) => {
        const existingItemsIds = prev.map((item) => item.id);
        const newItems = markets
          ?.filter((item) => !existingItemsIds.includes(item.id))
          ?.map((item) => ({
            id: item.id,
            isCollapsedMode: item.isCollapsedMode ?? false,
          }));
        return [...prev, ...newItems];
      });
    }
  }, [markets]);

  const toggleRow = useCallback((id: string) => {
    setRowsCollapsedMode((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isCollapsedMode: !item.isCollapsedMode } : item))
    );
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }, []);

  const handleSelectRow = useCallback(
    (market: Market | MarketsGroupedByMarketType) => {
      const isMarketSelected = selectedRows.includes(market.id);

      const toggleSelection = (idsToToggle: string[]) => {
        setSelectedRows((prev) =>
          isMarketSelected ? prev.filter((id) => !idsToToggle.includes(id)) : [...prev, ...idsToToggle]
        );
      };

      if (isGroupOfMarkets(market)) {
        const marketIds = market.markets.map((item) => item.id);
        toggleSelection([...marketIds, market.id]);
      } else {
        toggleSelection([market.id]);
      }
    },
    [selectedRows]
  );

  const handleMarketContextMenu = useCallback(
    (event: React.MouseEvent, row: Market) => {
      event.preventDefault();
      setContextMenu({
        mouseX: event.clientX - 2,
        mouseY: event.clientY - 4,
      });
      if (!selectedRows.includes(row.id)) {
        handleSelectRow(row);
      }
    },
    [selectedRows, handleSelectRow]
  );

  const handleCloseMarketMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleStatusUpdate = useCallback(
    (id: string, isActive: boolean) => {
      createMarketChangesData(
        {
          marketId: id,
          isActive: !isActive,
        },
        () => invalidateData([QUERY_KEYS.events])
      );
    },
    [createMarketChangesData, invalidateData]
  );

  const handleAddSpecialValue = useCallback(
    (
      eventId: string,
      row: {
        eventId: string;
        participants: Participant[];
        marketTypeId: string;
        specialValues: SpecialValue[];
      }
    ) => {
      openModal(
        <SpecialValueForm
          item={{
            eventId: eventId,
            participants: event.participants,
            marketTypeId: row.marketTypeId,
            specialValues: row.specialValues,
          }}
        />
      );
    },
    [openModal, event.participants]
  );

  const topLevelMarkets = useMemo(() => formatMarketsData(markets), [markets]);

  const handleSelectAllRows = useCallback(() => {
    if (selectedRows.length > 0) {
      setSelectedRows([]);
    } else {
      let marketsToSelect: string[] = [];
      topLevelMarkets.forEach((item) => {
        if (isGroupOfMarkets(item)) {
          marketsToSelect = [...marketsToSelect, ...item.markets.map((item) => item.id)];
        }
        marketsToSelect.push(item.id);
      });

      setSelectedRows(marketsToSelect);
    }
  }, [selectedRows.length, topLevelMarkets]);

  const getIsRowCollapsed = (id: string) => rowsCollapsedMode.find((item) => item.id === id)?.isCollapsedMode;

  return (
    <Fragment>
      {isLoading ? (
        <CircularProgress sx={{ position: 'absolute', top: '50%', left: '72%', transform: 'translate(-50%, -50%)' }} />
      ) : (
        <TableContainer sx={styles.tableContainer}>
          <Table aria-label='markets table' stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell component='th' align='center'>
                  <Checkbox
                    onChange={handleSelectAllRows}
                    checked={topLevelMarkets.every((item) => selectedRows.includes(item.id))}
                  />
                </StyledTableCell>
                <StyledTableCell component='th' align='center'>
                  Market Name
                </StyledTableCell>
                <StyledTableCell component='th' align='center'>
                  Status
                </StyledTableCell>
                <StyledTableCell component='th' align='center' />
              </TableRow>
            </TableHead>
            <TableBody sx={{ overflowY: 'auto' }}>
              {topLevelMarkets.map((row) => {
                return (
                  <Fragment key={row.id}>
                    <TableRow key={row.id} sx={selectedRows.includes(row.id) ? styles.mainRowSelected : {}} hover>
                      <StyledTableCell scope='row' align='center' sx={styles.selectTableCell}>
                        <Checkbox
                          onChange={() => handleSelectRow(row as Market)}
                          checked={selectedRows.includes(row.id)}
                        />
                      </StyledTableCell>
                      <StyledTableCell
                        scope='row'
                        align='left'
                        onClick={(event) => handleMarketContextMenu(event, row as Market)}
                        sx={styles.toggleRowCell}
                      >
                        <Box sx={styles.marketNameCellContent}>
                          <Stack
                            direction='row'
                            sx={{
                              alignItems: 'center',
                            }}
                          >
                            {getLatestValue(event, 'manualControl') ||
                            getLatestValue(row as Market, 'manualControl') ? (
                              <ManualIcon width='16' height='16' viewBox='4 4 16 16' />
                            ) : null}
                            {'publishStatus' in row && 'changed' in row && (
                              <PublishStatusIcon status={row.publishStatus} changed={row.changed} />
                            )}
                          </Stack>
                          <Typography variant='h6'>{row.name}</Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell align='center'>
                        <Box sx={styles.switchWrapper}>
                          {'isActive' in row && (
                            <Switch
                              value={getLatestValue(row, 'isActive')}
                              onChange={() => handleStatusUpdate(row.id, getLatestValue(row, 'isActive'))}
                              name='status'
                            />
                          )}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell align='right' sx={styles.toggleRowCell}>
                        <Box sx={styles.arrowIconWrapper}>
                          {!isEmpty(row.specialValues) && (
                            <Tooltip title='Add special value' placement='top'>
                              <IconButton
                                color='primary'
                                aria-label='add special value'
                                onClick={() =>
                                  handleAddSpecialValue(event.id, {
                                    eventId: event.id,
                                    participants: event.participants,
                                    marketTypeId: isGroupOfMarkets(row) ? row.marketType : row.marketTypeId,
                                    specialValues: row.specialValues,
                                  })
                                }
                              >
                                <AddCircle />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton
                            aria-label='expand row'
                            size='small'
                            onClick={() => toggleRow(row.id)}
                            sx={styles.toggleRowCell}
                          >
                            {!getIsRowCollapsed(row.id) ? <ArrowDropDownIcon /> : <ArrowDropUp />}
                          </IconButton>
                        </Box>
                      </StyledTableCell>
                    </TableRow>
                    {!getIsRowCollapsed(row.id) && (
                      <TableRow>
                        <TableCell sx={styles.outcomeTableCell} colSpan={4}>
                          <Box sx={styles.outcomeTableWrapper}>
                            <OutcomesTable
                              event={event}
                              markets={isGroupOfMarkets(row) ? row.markets : [row]}
                              queryKey={queryKey}
                              key={row.id}
                              displayType={isGroupOfMarkets(row) ? 'multiple' : 'single'}
                              selectedRows={selectedRows}
                              handleSelectRow={handleSelectRow}
                              handleMarketContextMenu={handleMarketContextMenu}
                              handleMarketStatusUpdate={handleStatusUpdate}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
          {isFetchingNextPage ? (
            <Stack
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                p: 1,
              }}
            >
              <CircularProgress />
            </Stack>
          ) : (
            <Box
              ref={lastElementRef}
              sx={{
                height: 56,
              }}
            />
          )}
        </TableContainer>
      )}
      {selectedRows.length > 0 && (
        <ManageMarketMenu
          contextMenu={contextMenu}
          handleClose={handleCloseMarketMenu}
          queryKey={queryKey}
          selectedRows={selectedRows}
          isLiveEvent={event.isLive}
          isOutrightEvent={event.type === EVENT_TYPE.OUTRIGHT}
        />
      )}
    </Fragment>
  );
};

export default MarketsTable;
